/**
 * ContentOsAgentsV4Glass — Content OS × ReelStack Glass Iridescent
 * Family: glass | Script: BoardCopy 10-scene ~45s | Audio: agents-v1
 */
import React from "react";
import { AbsoluteFill, Audio, Img, Sequence, interpolate, spring, staticFile, useCurrentFrame } from "remotion";
import { CausticBlobs, HairlineGrid, GlassCard, EyebrowPill, StaggeredWords, LightBeam, FloatingGlyphs, IridescentRing, ParticleBurst } from "@devinilabs/reelstack/families/glass/components";
import { palette as C, gradients } from "@devinilabs/reelstack/families/glass";

const CLAMP = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const sp = (frame: number, delay: number, cfg = { damping: 18, stiffness: 110 }) =>
  spring({ frame: frame - delay, fps: 30, config: cfg });
const FONT = "'Geist', 'Inter', system-ui, sans-serif";
const MONO = "'Geist Mono', 'JetBrains Mono', monospace";
const SAFE_TOP = 290;
const HAIRLINE = "rgba(14,14,18,0.10)";

export const BEAT = {
  hook: 0, problem: 80, timeline: 240, product: 390,
  mechanism: 540, cred: 660, numbers: 840, objection: 1020,
  epiphany: 1140, cta: 1230, end: 1350,
} as const;

// ─── CAPTION BAR ────────────────────────────────────────────────
type CW = { text: string; frame: number };
const CaptionBar: React.FC<{ words: CW[] }> = ({ words }) => {
  const f = useCurrentFrame();
  const ai = words.reduce((a, w, i) => (f >= w.frame ? i : a), -1);
  if (ai < 0) return null;
  return (
    <div style={{ position: "absolute", bottom: 80, left: 48, right: 48,
      background: "rgba(14,14,18,0.72)", backdropFilter: "blur(20px)",
      borderRadius: 16, padding: "18px 28px",
      display: "flex", flexWrap: "wrap", gap: "0 10px", justifyContent: "center" }}>
      {words.map((w, i) => (
        <span key={i} style={{ fontFamily: FONT, fontSize: 28, fontWeight: 500,
          color: i === ai ? C.iriGold : i < ai ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.18)" }}>
          {w.text}
        </span>
      ))}
    </div>
  );
};

// ─── HOOK ───────────────────────────────────────────────────────
const HookScene: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: C.bg }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.06} />
      <LightBeam color={C.iriViolet} cx="50%" cy="28%" />
      <FloatingGlyphs count={10} color={C.inkMuted} />
      <div style={{ position: "absolute", top: SAFE_TOP, left: 60 }}>
        <EyebrowPill startFrame={4} tint="violet">Content OS</EyebrowPill>
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 420 }}>
        <StaggeredWords text="Você acordou." startFrame={8} fontSize={88}
          fontWeight={900} color={C.ink} letterSpacing={-3} />
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 580 }}>
        <StaggeredWords text="Os agents não dormiram." startFrame={22} fontSize={52}
          fontWeight={500} color={C.inkSoft} letterSpacing={-1} />
      </div>
      <CaptionBar words={[
        { text: "Você", frame: 4 }, { text: "acordou.", frame: 14 },
        { text: "Os", frame: 26 }, { text: "agents", frame: 33 },
        { text: "não", frame: 41 }, { text: "dormiram.", frame: 50 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── PROBLEM ─────────────────────────────────────────────────────
const ProblemScene: React.FC = () => {
  const f = useCurrentFrame();
  const p1 = sp(f, BEAT.problem + 10, { damping: 20, stiffness: 100 });
  const p2 = sp(f, BEAT.problem + 40, { damping: 20, stiffness: 100 });
  return (
    <AbsoluteFill style={{ background: C.bgWarm }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.05} />
      <LightBeam color={C.iriRose} cx="30%" cy="50%" />
      <div style={{ position: "absolute", left: 60, right: 60, top: 360,
        opacity: p1, transform: `translateY(${interpolate(p1,[0,1],[40,0],CLAMP)}px)` }}>
        <GlassCard style={{ padding: "36px 40px" }}>
          <div style={{ fontFamily: MONO, fontSize: 14, color: C.inkMuted,
            letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 16 }}>SUA REALIDADE</div>
          <div style={{ fontFamily: FONT, fontSize: 64, fontWeight: 900,
            color: C.ink, lineHeight: 1, letterSpacing: "-0.04em" }}>
            1×<span style={{ fontSize: 32, fontWeight: 400, color: C.inkSoft }}> semana</span>
          </div>
          <div style={{ fontFamily: FONT, fontSize: 22, color: C.inkMuted, marginTop: 12 }}>
            porque não tem tempo
          </div>
        </GlassCard>
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 680,
        opacity: p2, transform: `translateY(${interpolate(p2,[0,1],[40,0],CLAMP)}px)` }}>
        <GlassCard style={{ padding: "28px 40px",
          background: "rgba(232,155,196,0.18)", border: "1.5px solid rgba(232,155,196,0.45)" }}>
          <div style={{ fontFamily: FONT, fontSize: 26, fontWeight: 600, color: C.inkSoft }}>
            O algoritmo te pune.
          </div>
          <div style={{ fontFamily: FONT, fontSize: 20, color: C.inkMuted, marginTop: 8 }}>
            E você sabe disso.
          </div>
        </GlassCard>
      </div>
      <CaptionBar words={[
        { text: "Você", frame: BEAT.problem+4 }, { text: "posta", frame: BEAT.problem+11 },
        { text: "uma", frame: BEAT.problem+18 }, { text: "vez", frame: BEAT.problem+23 },
        { text: "por", frame: BEAT.problem+28 }, { text: "semana", frame: BEAT.problem+34 },
        { text: "porque", frame: BEAT.problem+44 }, { text: "não", frame: BEAT.problem+52 },
        { text: "tem", frame: BEAT.problem+57 }, { text: "tempo.", frame: BEAT.problem+62 },
        { text: "O", frame: BEAT.problem+78 }, { text: "algoritmo", frame: BEAT.problem+84 },
        { text: "te", frame: BEAT.problem+96 }, { text: "pune.", frame: BEAT.problem+102 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── TIMELINE ────────────────────────────────────────────────────
const TimelineScene: React.FC = () => {
  const f = useCurrentFrame();
  const entries = [
    { time: "02:00", label: "copy gerado pelos agents", delay: 10 },
    { time: "03:00", label: "vídeo renderizado e legendado", delay: 50 },
    { time: "07:00", label: "agendado · publicado", delay: 90 },
  ];
  return (
    <AbsoluteFill style={{ background: C.bgCool }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.05} />
      <LightBeam color={C.iriCyan} cx="70%" cy="35%" />
      <div style={{ position: "absolute", top: SAFE_TOP+20, left: 60 }}>
        <EyebrowPill startFrame={BEAT.timeline+4} tint="teal">Enquanto Você Dormia</EyebrowPill>
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 420 }}>
        <GlassCard style={{ padding: "32px 40px" }}>
          {entries.map((e, i) => {
            const p = sp(f, BEAT.timeline + e.delay, { damping: 22, stiffness: 110 });
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 32,
                padding: "18px 0", borderBottom: i < 2 ? `1px solid ${HAIRLINE}` : "none",
                opacity: p, transform: `translateX(${interpolate(p,[0,1],[-20,0],CLAMP)}px)` }}>
                <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 700,
                  color: C.iriCyan, minWidth: 90, letterSpacing: "-0.02em" }}>{e.time}</div>
                <div style={{ width: 1, height: 28, background: HAIRLINE }} />
                <div style={{ fontFamily: FONT, fontSize: 22, color: C.inkSoft }}>{e.label}</div>
                <div style={{ marginLeft: "auto" }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%",
                    background: C.iriCyan, boxShadow: `0 0 12px ${C.iriCyan}` }} />
                </div>
              </div>
            );
          })}
        </GlassCard>
      </div>
      <CaptionBar words={[
        { text: "Às", frame: BEAT.timeline+4 }, { text: "duas", frame: BEAT.timeline+12 },
        { text: "da", frame: BEAT.timeline+19 }, { text: "manhã,", frame: BEAT.timeline+25 },
        { text: "copy", frame: BEAT.timeline+38 }, { text: "pronto.", frame: BEAT.timeline+46 },
        { text: "Às", frame: BEAT.timeline+60 }, { text: "três,", frame: BEAT.timeline+67 },
        { text: "vídeo.", frame: BEAT.timeline+75 }, { text: "Às", frame: BEAT.timeline+90 },
        { text: "sete,", frame: BEAT.timeline+97 }, { text: "publicado.", frame: BEAT.timeline+108 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── PRODUCT ─────────────────────────────────────────────────────
const ProductScene: React.FC = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{ background: C.bg }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.06} />
      <LightBeam color={C.iriGold} cx="50%" cy="40%" />
      <div style={{ position: "absolute", top: SAFE_TOP+20, left: 60 }}>
        <EyebrowPill startFrame={BEAT.product+4} tint="violet">01 / O Sistema</EyebrowPill>
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 420 }}>
        <StaggeredWords text="Content OS." startFrame={BEAT.product+8}
          fontSize={96} fontWeight={900} color={C.ink} letterSpacing={-3} />
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 600,
        display: "flex", gap: 40, alignItems: "center" }}>
        {[
          { num: "15", label: "agents ativos", color: C.iriViolet, delay: 25 },
          { num: "0h",  label: "hora extra",    color: C.iriCyan,   delay: 42 },
        ].map((s, i) => {
          const p = sp(f, BEAT.product + s.delay, { damping: 20, stiffness: 120 });
          return (
            <React.Fragment key={i}>
              {i > 0 && <div style={{ width: 1, height: 80, background: HAIRLINE, flexShrink: 0 }} />}
              <div style={{ opacity: p, transform: `translateY(${interpolate(p,[0,1],[20,0],CLAMP)}px)` }}>
                <div style={{ fontFamily: FONT, fontSize: 72, fontWeight: 900,
                  color: s.color, lineHeight: 1, letterSpacing: "-0.04em" }}>{s.num}</div>
                <div style={{ fontFamily: MONO, fontSize: 14, color: C.inkMuted,
                  letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 8 }}>{s.label}</div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <CaptionBar words={[
        { text: "Content", frame: BEAT.product+4 }, { text: "OS.", frame: BEAT.product+13 },
        { text: "Quinze", frame: BEAT.product+26 }, { text: "agents.", frame: BEAT.product+36 },
        { text: "Zero", frame: BEAT.product+54 }, { text: "hora", frame: BEAT.product+64 },
        { text: "extra.", frame: BEAT.product+72 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── MECHANISM ───────────────────────────────────────────────────
const MechanismScene: React.FC = () => {
  const f = useCurrentFrame();
  const tags = ["copy","vídeo","legenda","agendamento","publicação"];
  return (
    <AbsoluteFill style={{ background: C.bgWarm }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.05} />
      <LightBeam color={C.iriRose} cx="40%" cy="60%" />
      <div style={{ position: "absolute", top: SAFE_TOP+20, left: 60 }}>
        <EyebrowPill startFrame={BEAT.mechanism+4} tint="violet">02 / Como Funciona</EyebrowPill>
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 420 }}>
        <StaggeredWords text="Um aprova." startFrame={BEAT.mechanism+6}
          fontSize={72} fontWeight={900} color={C.ink} letterSpacing={-3} />
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 540 }}>
        <StaggeredWords text="Eles fazem o resto." startFrame={BEAT.mechanism+22}
          fontSize={46} fontWeight={500} color={C.inkSoft} letterSpacing={-1} />
      </div>
      <div style={{ position: "absolute", left: 60, top: 710,
        display: "flex", flexWrap: "wrap", gap: 14 }}>
        {tags.map((tag, i) => {
          const p = sp(f, BEAT.mechanism + 40 + i * 8, { damping: 18, stiffness: 130 });
          return (
            <div key={i} style={{ background: i%2===0 ? "rgba(143,127,232,0.15)" : "rgba(133,221,201,0.15)",
              border: `1px solid ${i%2===0 ? C.violetPill : C.tealPill}44`,
              borderRadius: 9999, padding: "10px 24px",
              fontFamily: MONO, fontSize: 18,
              color: i%2===0 ? C.violetPill : C.tealPill,
              opacity: p, transform: `scale(${interpolate(p,[0,1],[0.8,1],CLAMP)})`
            }}>{tag}</div>
          );
        })}
      </div>
      <CaptionBar words={[
        { text: "Um", frame: BEAT.mechanism+4 }, { text: "aprova", frame: BEAT.mechanism+11 },
        { text: "a", frame: BEAT.mechanism+19 }, { text: "ideia.", frame: BEAT.mechanism+24 },
        { text: "Eles", frame: BEAT.mechanism+36 }, { text: "fazem", frame: BEAT.mechanism+44 },
        { text: "o", frame: BEAT.mechanism+52 }, { text: "resto.", frame: BEAT.mechanism+58 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── CRED ─────────────────────────────────────────────────────────
const CredScene: React.FC = () => {
  const f = useCurrentFrame();
  const p = sp(f, BEAT.cred+10, { damping: 18, stiffness: 100 });
  return (
    <AbsoluteFill style={{ background: C.bgCool }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.05} />
      <LightBeam color={C.iriGold} cx="60%" cy="45%" />
      <div style={{ position: "absolute", left: 60, right: 60, top: 360,
        opacity: p, transform: `translateY(${interpolate(p,[0,1],[30,0],CLAMP)}px)` }}>
        <GlassCard style={{ padding: "44px 44px" }}>
          <div style={{ display: "flex", gap: 28, marginBottom: 32 }}>
            <div style={{ width: 6, height: 80, borderRadius: 3,
              background: gradients.iridescent, flexShrink: 0 }} />
            <div>
              <div style={{ fontFamily: FONT, fontSize: 30, fontWeight: 700,
                color: C.ink, lineHeight: 1.3, letterSpacing: "-0.02em", marginBottom: 16 }}>
                Eu construí esse sistema para mim primeiro.
              </div>
              <div style={{ fontFamily: FONT, fontSize: 22, color: C.inkMuted, lineHeight: 1.5 }}>
                Quando funcionou, documentei cada agente e transformei em blueprint.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {["Construído pelo fundador","Documentado","Replicável"].map((tag, i) => {
              const tp = sp(f, BEAT.cred + 80 + i*12);
              return (
                <div key={i} style={{ background: "rgba(143,127,232,0.12)",
                  border: `1px solid ${C.violetPill}44`,
                  borderRadius: 9999, padding: "8px 20px",
                  fontFamily: MONO, fontSize: 14, color: C.violetPill, letterSpacing: "0.1em",
                  opacity: tp, transform: `translateY(${interpolate(tp,[0,1],[10,0],CLAMP)}px)`
                }}>{tag}</div>
              );
            })}
          </div>
        </GlassCard>
      </div>
      <CaptionBar words={[
        { text: "Eu", frame: BEAT.cred+4 }, { text: "construí", frame: BEAT.cred+12 },
        { text: "esse", frame: BEAT.cred+22 }, { text: "sistema", frame: BEAT.cred+30 },
        { text: "para", frame: BEAT.cred+39 }, { text: "mim", frame: BEAT.cred+46 },
        { text: "primeiro.", frame: BEAT.cred+52 }, { text: "Quando", frame: BEAT.cred+72 },
        { text: "funcionou,", frame: BEAT.cred+82 }, { text: "documentei", frame: BEAT.cred+95 },
        { text: "cada", frame: BEAT.cred+108 }, { text: "agente", frame: BEAT.cred+116 },
        { text: "e", frame: BEAT.cred+126 }, { text: "transformei", frame: BEAT.cred+132 },
        { text: "em", frame: BEAT.cred+144 }, { text: "blueprint.", frame: BEAT.cred+150 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── NUMBERS ─────────────────────────────────────────────────────
const NumbersScene: React.FC = () => {
  const f = useCurrentFrame();
  const stats = [
    { display: "47",    label: "reels publicados automaticamente", color: C.ink,       delay: 8  },
    { display: "1/sem", label: "única aprovação sua",              color: C.iriViolet, delay: 55 },
    { display: "+312%", label: "alcance orgânico",                 color: C.iriCyan,   delay: 105 },
  ];
  return (
    <AbsoluteFill style={{ background: C.bg }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.06} />
      <LightBeam color={C.iriCyan} cx="50%" cy="30%" />
      <ParticleBurst cx={540} cy={300} startFrame={BEAT.numbers+8} count={14}
        colors={[C.iriCyan, C.iriViolet, C.iriGold]} />
      <div style={{ position: "absolute", top: SAFE_TOP+20, left: 60 }}>
        <EyebrowPill startFrame={BEAT.numbers+4} tint="teal">03 / Os Números</EyebrowPill>
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 380 }}>
        {stats.map((s, i) => {
          const p = sp(f, BEAT.numbers + s.delay, { damping: 18, stiffness: 110 });
          return (
            <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 24,
              padding: "26px 0", borderBottom: i < 2 ? `1px solid ${HAIRLINE}` : "none",
              opacity: p, transform: `translateX(${interpolate(p,[0,1],[-24,0],CLAMP)}px)` }}>
              <div style={{ fontFamily: FONT, fontSize: 80, fontWeight: 900,
                color: s.color, lineHeight: 1, letterSpacing: "-0.04em", minWidth: 200 }}>
                {s.display}
              </div>
              <div style={{ fontFamily: FONT, fontSize: 20, color: C.inkMuted }}>{s.label}</div>
            </div>
          );
        })}
      </div>
      <CaptionBar words={[
        { text: "Quarenta", frame: BEAT.numbers+4 }, { text: "e", frame: BEAT.numbers+14 },
        { text: "sete", frame: BEAT.numbers+19 }, { text: "reels.", frame: BEAT.numbers+27 },
        { text: "Uma", frame: BEAT.numbers+47 }, { text: "aprovação", frame: BEAT.numbers+56 },
        { text: "por", frame: BEAT.numbers+68 }, { text: "semana.", frame: BEAT.numbers+75 },
        { text: "Mais", frame: BEAT.numbers+96 }, { text: "trezentos", frame: BEAT.numbers+104 },
        { text: "e", frame: BEAT.numbers+117 }, { text: "doze", frame: BEAT.numbers+122 },
        { text: "por", frame: BEAT.numbers+130 }, { text: "cento.", frame: BEAT.numbers+138 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── OBJECTION ───────────────────────────────────────────────────
const ObjectionScene: React.FC = () => {
  const f = useCurrentFrame();
  const p = sp(f, BEAT.objection+8, { damping: 18, stiffness: 110 });
  return (
    <AbsoluteFill style={{ background: C.bgWarm }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.05} />
      <LightBeam color={C.iriViolet} cx="50%" cy="50%" />
      <div style={{ position: "absolute", left: 60, right: 60,
        top: 0, bottom: 0, display: "flex", flexDirection: "column",
        justifyContent: "center", paddingBottom: 160,
        opacity: p, transform: `scale(${interpolate(p,[0,1],[0.97,1],CLAMP)})` }}>
        <StaggeredWords text="Não precisa" startFrame={BEAT.objection+6}
          fontSize={72} fontWeight={900} color={C.inkMuted} letterSpacing={-3} />
        <StaggeredWords text="saber de IA." startFrame={BEAT.objection+18}
          fontSize={72} fontWeight={900} color={C.ink} letterSpacing={-3} />
        <div style={{ marginTop: 32 }}>
          <StaggeredWords text="Precisa de um sistema que já funciona."
            startFrame={BEAT.objection+40} fontSize={30} fontWeight={500}
            color={C.inkSoft} letterSpacing={-0.5} />
        </div>
      </div>
      <CaptionBar words={[
        { text: "Você", frame: BEAT.objection+4 }, { text: "não", frame: BEAT.objection+11 },
        { text: "precisa", frame: BEAT.objection+18 }, { text: "saber", frame: BEAT.objection+27 },
        { text: "de", frame: BEAT.objection+34 }, { text: "IA.", frame: BEAT.objection+40 },
        { text: "Precisa", frame: BEAT.objection+55 }, { text: "de", frame: BEAT.objection+63 },
        { text: "um", frame: BEAT.objection+68 }, { text: "sistema", frame: BEAT.objection+74 },
        { text: "que", frame: BEAT.objection+83 }, { text: "já", frame: BEAT.objection+89 },
        { text: "funciona.", frame: BEAT.objection+95 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── EPIPHANY ─────────────────────────────────────────────────────
const EpiphanyScene: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: C.bg }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.04} />
      <LightBeam color={C.iriCyan} cx="50%" cy="50%" />
      <LightBeam color={C.iriViolet} cx="20%" cy="70%" />
      <div style={{ position: "absolute", left: 60, right: 60,
        top: 0, bottom: 0, display: "flex", flexDirection: "column",
        justifyContent: "center", paddingBottom: 160 }}>
        <StaggeredWords text="Um sistema que" startFrame={BEAT.epiphany+4}
          fontSize={52} fontWeight={500} color={C.inkMuted} letterSpacing={-1.3} />
        <StaggeredWords text="trabalha enquanto" startFrame={BEAT.epiphany+14}
          fontSize={72} fontWeight={900} color={C.ink} letterSpacing={-3} />
        <StaggeredWords text="você dorme." startFrame={BEAT.epiphany+26}
          fontSize={72} fontWeight={900} color={C.iriViolet} letterSpacing={-3}
          />
      </div>
      <CaptionBar words={[
        { text: "Um", frame: BEAT.epiphany+4 }, { text: "sistema", frame: BEAT.epiphany+11 },
        { text: "que", frame: BEAT.epiphany+19 }, { text: "trabalha", frame: BEAT.epiphany+26 },
        { text: "enquanto", frame: BEAT.epiphany+38 }, { text: "você", frame: BEAT.epiphany+52 },
        { text: "dorme.", frame: BEAT.epiphany+61 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── CTA ──────────────────────────────────────────────────────────
const CTAScene: React.FC = () => {
  const f = useCurrentFrame();
  const ringP  = sp(f, BEAT.cta,    { damping: 16, stiffness: 90 });
  const textP  = sp(f, BEAT.cta+10, { damping: 18, stiffness: 110 });
  const wordP  = sp(f, BEAT.cta+22, { damping: 18, stiffness: 120 });
  const hdlP   = sp(f, BEAT.cta+38, { damping: 20, stiffness: 100 });
  return (
    <AbsoluteFill style={{ background: C.bgCool }}>
      <CausticBlobs />
      <HairlineGrid strokeOpacity={0.06} />
      <LightBeam color={C.iriGold} cx="50%" cy="35%" />
      <LightBeam color={C.iriViolet} cx="80%" cy="65%" />
      <IridescentRing cx={540} cy={SAFE_TOP+92} count={3} period={180}
        minRadius={80} maxRadius={280}
        colors={[C.iriCyan, C.iriViolet, C.iriRose, C.iriGold]} />
      <div style={{ position: "absolute", top: SAFE_TOP+20, left: 0, right: 0,
        display: "flex", justifyContent: "center" }}>
        <div style={{ position: "relative", width: 144, height: 144,
          opacity: ringP, transform: `scale(${interpolate(ringP,[0,1],[0.7,1],CLAMP)})` }}>
          <div style={{ width: 144, height: 144, borderRadius: "50%",
            overflow: "hidden", border: `3px solid ${C.iriViolet}` }}>
            <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        </div>
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 530,
        textAlign: "center",
        opacity: textP, transform: `translateY(${interpolate(textP,[0,1],[20,0],CLAMP)}px)` }}>
        <div style={{ fontFamily: FONT, fontSize: 36, fontWeight: 600,
          color: C.inkSoft, marginBottom: 28, letterSpacing: "-0.02em" }}>
          Quer construir o seu?
        </div>
        <div style={{ fontFamily: MONO, fontSize: 18, color: C.inkMuted,
          letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 14 }}>Comenta</div>
        <div style={{ fontFamily: FONT, fontSize: 96, fontWeight: 900,
          color: C.iriViolet, lineHeight: 0.9, letterSpacing: "-0.04em",
          opacity: wordP, transform: `scale(${interpolate(wordP,[0,1],[0.85,1],CLAMP)})` }}>
          AGENTE
        </div>
      </div>
      <div style={{ position: "absolute", bottom: SAFE_TOP, left: 0, right: 0,
        display: "flex", justifyContent: "center",
        opacity: interpolate(hdlP,[0,1],[0,0.45],CLAMP) }}>
        <div style={{ fontFamily: MONO, fontSize: 16, color: C.inkMuted, letterSpacing: "0.1em" }}>
          @guilhermeribeiro.ai
        </div>
      </div>
      <CaptionBar words={[
        { text: "Quer", frame: BEAT.cta+4 }, { text: "construir", frame: BEAT.cta+12 },
        { text: "o", frame: BEAT.cta+24 }, { text: "seu?", frame: BEAT.cta+29 },
        { text: "Comenta", frame: BEAT.cta+42 }, { text: "AGENTE.", frame: BEAT.cta+56 },
      ]} />
    </AbsoluteFill>
  );
};

// ─── ROOT ────────────────────────────────────────────────────────
export const ContentOsAgentsV4Glass: React.FC = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill>
      <Sequence from={BEAT.hook}      durationInFrames={80}  ><Audio src={staticFile("audio/agents-v1/ag01.mp3")}  startFrom={0}/></Sequence>
      <Sequence from={BEAT.problem}   durationInFrames={160} ><Audio src={staticFile("audio/agents-v1/ag01b.mp3")} startFrom={0}/></Sequence>
      <Sequence from={BEAT.timeline}  durationInFrames={150} ><Audio src={staticFile("audio/agents-v1/ag02.mp3")}  startFrom={0}/></Sequence>
      <Sequence from={BEAT.product}   durationInFrames={150} ><Audio src={staticFile("audio/agents-v1/ag03.mp3")}  startFrom={0}/></Sequence>
      <Sequence from={BEAT.mechanism} durationInFrames={120} ><Audio src={staticFile("audio/agents-v1/ag04.mp3")}  startFrom={0}/></Sequence>
      <Sequence from={BEAT.cred}      durationInFrames={180} ><Audio src={staticFile("audio/agents-v1/ag04b.mp3")} startFrom={0}/></Sequence>
      <Sequence from={BEAT.numbers}   durationInFrames={180} ><Audio src={staticFile("audio/agents-v1/ag05.mp3")}  startFrom={0}/></Sequence>
      <Sequence from={BEAT.objection} durationInFrames={120} ><Audio src={staticFile("audio/agents-v1/ag05b.mp3")} startFrom={0}/></Sequence>
      <Sequence from={BEAT.epiphany}  durationInFrames={90}  ><Audio src={staticFile("audio/agents-v1/ag06.mp3")}  startFrom={0}/></Sequence>
      <Sequence from={BEAT.cta}       durationInFrames={120} ><Audio src={staticFile("audio/agents-v1/ag07.mp3")}  startFrom={0}/></Sequence>
      {f >= BEAT.hook      && f < BEAT.problem   && <HookScene />}
      {f >= BEAT.problem   && f < BEAT.timeline  && <ProblemScene />}
      {f >= BEAT.timeline  && f < BEAT.product   && <TimelineScene />}
      {f >= BEAT.product   && f < BEAT.mechanism && <ProductScene />}
      {f >= BEAT.mechanism && f < BEAT.cred      && <MechanismScene />}
      {f >= BEAT.cred      && f < BEAT.numbers   && <CredScene />}
      {f >= BEAT.numbers   && f < BEAT.objection && <NumbersScene />}
      {f >= BEAT.objection && f < BEAT.epiphany  && <ObjectionScene />}
      {f >= BEAT.epiphany  && f < BEAT.cta       && <EpiphanyScene />}
      {f >= BEAT.cta       && f < BEAT.end       && <CTAScene />}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
